import { FC, Fragment, useReducer } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { MasterKeyForm } from '../form';

import { masterKeyService } from 'v2/api/masterKeyService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { USER_ROLES, UserRole } from 'v2/constants';
import { handleHttpError } from 'v2/utils';

interface CreateMasterKeyButtonProps {
  onSuccess: VoidFunction;
}

export const CreateMasterKeyButton: FC<CreateMasterKeyButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger, isMutating } = useSWRMutation('createMasterKey', masterKeyService.createMasterKey, {
    onSuccess: () => {
      onSuccess();
      toast.success('The master key has been successfully created');
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<MasterKeyForm>({
    initialValues: { name: '', roles: [] },
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleChangeRoles = (values: UserRole[]) => {
    formik.setFieldValue('roles', values);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen();
  };

  return (
    <Fragment>
      <Button variant="contained" startIcon={<Plus />} onClick={setOpen} size="large">
        Create Master Key
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create Master Key</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form">
            <TextField
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Name"
              required
              fullWidth
            />

            <CheckBoxAutocomplete
              label="Roles"
              value={formik.values.roles}
              options={USER_ROLES}
              onChange={handleChangeRoles}
              required
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
